import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import { CreditCard, FileClock, LayoutDashboard, ListMusic, Plus, UserRound } from 'lucide-react';
import {
  SidebarCollapseIcon,
  SidebarExpandIcon,
} from '../../Assets/Icons';
import { imageLink } from '../../Constant/Utils';

import logo from './../../Assets/Images/songplace.png';
import logoIcon from './../../Assets/Images/logo.png';
import { Equalizer } from '../ui/music';
import './sidebar.scss';

const Sidebar = ({ onClose, collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth0User = useSelector(e => e.authenticationSlice.auth0User);
  const user = useSelector(e => e.authenticationSlice.user);
  const pathname = location.pathname;

  const navButton = (path, label, iconContent, tooltipLabel) => {
    const isActive = pathname === path;
    return (
      <Tooltip title={collapsed ? tooltipLabel || label : ''} placement="right">
        <button
          type="button"
          className={isActive ? 'navItem active' : 'navItem'}
          aria-current={isActive ? 'page' : undefined}
          onClick={() => {
            navigate(path);
            if (onClose) onClose();
          }}
        >
          <div className="navIconWrapper">{iconContent}</div>
          <span className="navText">
            <span className="navLabel">{label}</span>
            {isActive && !collapsed ? (
              <Equalizer playing className="navEq" bars={3} />
            ) : null}
          </span>
        </button>
      </Tooltip>
    );
  };

  return (
    <div className={`adminSidebarNew artistSidebarNew ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebarHeader">
        <div className="logoWrapper">
          <img src={collapsed ? logoIcon : logo} alt="Songplace" className={collapsed ? 'logoIcon' : ''} />
        </div>
        {!onClose && (
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <button
              type="button"
              className="collapseToggleBtn"
              onClick={onToggle}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <SidebarExpandIcon /> : <SidebarCollapseIcon />}
            </button>
          </Tooltip>
        )}
      </div>

      <nav className="sidebarNav" aria-label="Artist dashboard">
        <div className="navSection">
          {!collapsed && <span className="sectionLabel">Overview</span>}
          {navButton('/dashboard', 'Dashboard', <LayoutDashboard size={18} strokeWidth={2} aria-hidden />, 'Dashboard')}
        </div>

        <div className="navSection">
          {!collapsed && <span className="sectionLabel">Campaigns</span>}
          {navButton(
            '/dashboard/view-campaigns',
            'Campaigns',
            <ListMusic size={18} strokeWidth={2} aria-hidden />,
            'Campaigns'
          )}
          {navButton(
            '/dashboard/drafts',
            'Drafts',
            <FileClock size={18} strokeWidth={2} aria-hidden />,
            'Drafts'
          )}

          <Tooltip title={collapsed ? 'New campaign' : ''} placement="right">
            <button
              type="button"
              className={`createCampaignBtn ${collapsed ? 'collapsed' : ''}`}
              onClick={() => {
                navigate('/create-campaign/add-track');
                if (onClose) onClose();
              }}
            >
              {collapsed ? (
                <Plus size={20} strokeWidth={2.5} aria-hidden />
              ) : (
                <>
                  <Plus size={18} strokeWidth={2.5} aria-hidden />
                  <span>New campaign</span>
                </>
              )}
            </button>
          </Tooltip>
        </div>

        <div className="navSection">
          {!collapsed && <span className="sectionLabel">Account</span>}
          {navButton(
            '/dashboard/billing',
            'Billing',
            <CreditCard size={18} strokeWidth={2} aria-hidden />,
            'Billing'
          )}
          {navButton(
            '/dashboard/account-overview',
            'Account',
            <UserRound size={18} strokeWidth={2} aria-hidden />,
            'Account'
          )}
        </div>
      </nav>

      <div
        className={`userProfileCard ${collapsed ? 'collapsed' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => navigate('/dashboard/account-overview')}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigate('/dashboard/account-overview');
          }
        }}
        aria-label="Open account overview"
      >
        <div className="userAvatar">
          <img src={imageLink(user?.image || auth0User?.picture)} alt="" />
          <span className="statusDot" aria-hidden />
        </div>
        {!collapsed ? (
          <div className="userInfo">
            <p className="userName">{user?.name || 'Artist'}</p>
            <span className="userRole">Artist</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Sidebar;
